import React, { useEffect, useMemo, useState } from 'react';
import { api, type Room, type Bed, type CleaningNote, type BookingCounts } from '../lib/api';
import { fmtClock } from '../lib/format';

type StatusFilter = 'all' | 'clean' | 'dirty';
type OccupancyFilter = 'all' | 'vacant' | 'occupied' | 'blocked';

interface Props {
  rooms: Room[];
  onStatusClick: (room: Room) => void;
  onHistoryClick: (roomId: string) => void;
  onNotesClick: (room: Room) => void;
  /** Bumped by the caller to force a reload after notes change. */
  refreshToken?: number;
}

function bedSummary(beds: Bed[]): { total: number; occupied: number; blocked: number; free: number; types: string } {
  let occupied = 0, blocked = 0;
  for (const b of beds) {
    if (b.currentStay) occupied++;
    else if (b.isBlocked) blocked++;
  }
  const free = beds.length - occupied - blocked;
  const types = beds.map(b => b.bedType.charAt(0)).join('');
  return { total: beds.length, occupied, blocked, free, types };
}

function occupancyLabel(s: ReturnType<typeof bedSummary>): { text: string; cls: string } {
  // A booking-only room has no beds, so occupancy is not a thing it has.
  // "0 free" would read as a full room rather than an inapplicable question.
  if (s.total === 0) return { text: 'bookings only', cls: 'free' };
  if (s.occupied > 0) return { text: `${s.occupied}/${s.total} occupied`, cls: 'occupied' };
  if (s.blocked  > 0) return { text: `${s.blocked}/${s.total} blocked`,   cls: 'blocked'  };
  return { text: `${s.total} free`, cls: 'free' };
}

export function HousekeepingScreen({ rooms, onStatusClick, onHistoryClick, onNotesClick, refreshToken = 0 }: Props) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [occFilter,    setOccFilter]    = useState<OccupancyFilter>('all');

  // Room Tracker tie-in: last night's booking count per room, and any open
  // cleaning notes. Both are read-only here — no thresholds, no flagging, and
  // nothing here changes a room's housekeeping status.
  const [bookingCounts, setBookingCounts] = useState<BookingCounts | null>(null);
  const [openNotes, setOpenNotes] = useState<CleaningNote[]>([]);
  const [tieInError, setTieInError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.bookingCounts(), api.cleaningNotes(false)])
      .then(([counts, notes]) => {
        if (cancelled) return;
        setBookingCounts(counts);
        setOpenNotes(notes.notes);
        setTieInError(null);
      })
      .catch(e => { if (!cancelled) setTieInError(e instanceof Error ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, [refreshToken]);

  const countByRoom = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of bookingCounts?.counts ?? []) m.set(c.roomId, c.count);
    return m;
  }, [bookingCounts]);

  const notesByRoom = useMemo(() => {
    const m = new Map<string, number>();
    for (const n of openNotes) m.set(n.roomId, (m.get(n.roomId) ?? 0) + 1);
    return m;
  }, [openNotes]);

  const filtered = useMemo(() => {
    return rooms.filter(r => {
      const s = bedSummary(r.beds);
      if (statusFilter === 'clean' && r.housekeepingStatus !== 'CLEAN') return false;
      if (statusFilter === 'dirty' && r.housekeepingStatus !== 'DIRTY') return false;
      if (occFilter === 'vacant'   && (s.occupied > 0 || s.blocked > 0)) return false;
      if (occFilter === 'occupied' && s.occupied === 0) return false;
      if (occFilter === 'blocked'  && s.blocked  === 0) return false;
      return true;
    });
  }, [rooms, statusFilter, occFilter]);

  const counts = useMemo(() => {
    let clean = 0, dirty = 0, vacant = 0, occupied = 0, blocked = 0;
    for (const r of rooms) {
      if (r.housekeepingStatus === 'CLEAN') clean++;
      else if (r.housekeepingStatus === 'DIRTY') dirty++;
      const s = bedSummary(r.beds);
      if (s.occupied > 0) occupied++;
      else if (s.blocked > 0) blocked++;
      else vacant++;
    }
    return { clean, dirty, vacant, occupied, blocked, total: rooms.length };
  }, [rooms]);

  return (
    <div className="hskp-screen">
      <div className="hskp-summary">
        <span className="hskp-summary-item">Total <strong>{counts.total}</strong></span>
        <span className="hskp-summary-item clean">Clean <strong>{counts.clean}</strong></span>
        <span className="hskp-summary-item dirty">Dirty <strong>{counts.dirty}</strong></span>
        <span className="hskp-summary-sep">|</span>
        <span className="hskp-summary-item">Vacant <strong>{counts.vacant}</strong></span>
        <span className="hskp-summary-item">Occupied <strong>{counts.occupied}</strong></span>
        <span className="hskp-summary-item">Blocked <strong>{counts.blocked}</strong></span>
      </div>

      <div className="hskp-filters">
        <div className="hskp-filter-group">
          <span className="hskp-filter-label">Status:</span>
          {(['all','clean','dirty'] as StatusFilter[]).map(f => (
            <button
              key={f}
              className={`hskp-chip ${statusFilter === f ? 'active' : ''}`}
              onClick={() => setStatusFilter(f)}
            >
              {f === 'all' ? 'All' : f === 'clean' ? '✓ Clean' : '⚠ Dirty'}
            </button>
          ))}
        </div>
        <div className="hskp-filter-group">
          <span className="hskp-filter-label">Occupancy:</span>
          {(['all','vacant','occupied','blocked'] as OccupancyFilter[]).map(f => (
            <button
              key={f}
              className={`hskp-chip ${occFilter === f ? 'active' : ''}`}
              onClick={() => setOccFilter(f)}
            >
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {tieInError && <div className="rt-error">Could not load booking counts or notes: {tieInError}</div>}

      <table className="hskp-table">
        <thead>
          <tr>
            <th>Room</th>
            <th>Beds</th>
            <th>Status</th>
            <th>Occupancy</th>
            <th title={
              bookingCounts
                ? `Bookings between ${fmtClock(bookingCounts.startsAt)} and ${fmtClock(bookingCounts.endsAt)}`
                : undefined
            }>
              Bookings
              {bookingCounts && <span className="hskp-col-sub">{bookingCounts.date}</span>}
            </th>
            <th>Cleaning notes</th>
            <th>Last changed</th>
            <th>By</th>
            <th>Note</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {filtered.map(r => {
            const s = bedSummary(r.beds);
            const occ = occupancyLabel(s);
            const isClean = r.housekeepingStatus === 'CLEAN';

            return (
              <tr key={r.id}>
                <td>
                  <strong>Room {r.number}</strong>
                  {r.displayName && <span style={{ color: 'var(--text-secondary)', marginLeft: 6, fontSize: 11 }}>{r.displayName}</span>}
                </td>
                <td>
                  {s.total === 0
                    ? <span style={{ color: 'var(--text-secondary)' }}>—</span>
                    : <span className="hskp-beds">{s.total} ({s.types})</span>}
                </td>
                <td>
                  <button
                    type="button"
                    className={`hskp-status-badge ${isClean ? 'clean' : 'dirty'}`}
                    onClick={() => onStatusClick(r)}
                    title="Click to change status"
                  >
                    {isClean ? '✓ Clean' : '⚠ Dirty'}
                  </button>
                </td>
                <td><span className={`hskp-occ ${occ.cls}`}>{occ.text}</span></td>
                <td>
                  {/* Display only. A high count is information for scheduling a
                      deep clean, not a flag — providers clean their own rooms
                      after each client, so this says nothing about turnover. */}
                  {countByRoom.get(r.id)
                    ? <span className="hskp-bookings">{countByRoom.get(r.id)}</span>
                    : <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                </td>
                <td>
                  <button
                    type="button"
                    className={`hskp-notes-btn ${notesByRoom.get(r.id) ? 'has-notes' : ''}`}
                    onClick={() => onNotesClick(r)}
                    title="View and add cleaning notes"
                  >
                    {notesByRoom.get(r.id)
                      ? `⚠ ${notesByRoom.get(r.id)} open`
                      : 'Add'}
                  </button>
                </td>
                <td>
                  {(r as any).housekeepingUpdatedAt
                    ? fmtClock((r as any).housekeepingUpdatedAt)
                    : <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                </td>
                <td>
                  {(r as any).housekeepingUpdatedBy ||
                    <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                </td>
                <td style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {(r as any).housekeepingNote ||
                    <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                </td>
                <td>
                  <button
                    type="button"
                    className="hskp-history-btn"
                    onClick={() => onHistoryClick(r.id)}
                    title="View housekeeping history"
                  >
                    History
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {filtered.length === 0 && (
        <div className="loading">No rooms match the current filters.</div>
      )}

      <div className="hskp-footer">
        Showing {filtered.length} of {rooms.length} rooms.
      </div>
    </div>
  );
}
